/**
 * BlueBean API連携の読み書き(画面側)。**画面から BlueBean には接続しない**。
 * 取得はサーバー(Cloud Functions `bbApiFetch`)に頼み、結果は bb_api_daily/{date} を読む。
 */
import { collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDb, getFirebaseFunctions } from '@core/lib/firebase';
import type { DateStr } from '@core/lib/types';
import { normalizeCode } from './activity';
import type { BbApiDaily } from './bluebeanApiDaily';
import type { BbApiLive } from './bluebeanApiLive';
import { windowsOfDoc, type CallWindow } from './workEstimate';

/** settings/bluebean_api */
export type BbApiSettings = {
  /** 業務コード → BlueBean の業務ID */
  campaigns: Record<string, number>;
  /** 毎朝6時に前日分を自動で取得する */
  schedule_enabled: boolean;
  /** 日中に本日分(速報)を取得する */
  live_enabled: boolean;
  /** 速報の間隔(分。5〜60の5の倍数)。既定 30 */
  live_every_min: number;
  /** 速報を動かす時間帯(JSTの時)。既定 9〜20 */
  live_from_hour: number;
  live_to_hour: number;
  /** 「締め」(その日を1日ぶん取り直す)を行う時刻(JSTの時)。既定 19 */
  finalize_hour: number;
};

/** 設定の既定値(サーバー側と同じ) */
export const BB_API_DEFAULTS = { live_every_min: 30, live_from_hour: 9, live_to_hour: 20, finalize_hour: 19 } as const;
const inRange = (v: unknown, min: number, max: number, fallback: number) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max ? n : fallback;
};

const SETTINGS_PATH = ['settings', 'bluebean_api'] as const;

export async function fetchBbApiSettings(): Promise<BbApiSettings> {
  const data = (await getDoc(doc(getDb(), ...SETTINGS_PATH))).data() ?? {};
  const campaigns: Record<string, number> = {};
  for (const [code, id] of Object.entries((data.campaigns ?? {}) as Record<string, unknown>)) {
    const n = Number(id);
    if (Number.isInteger(n) && n > 0) campaigns[normalizeCode(code)] = n;
  }
  return {
    campaigns,
    schedule_enabled: data.schedule_enabled === true,
    live_enabled: data.live_enabled === true,
    live_every_min: inRange(data.live_every_min, 5, 60, BB_API_DEFAULTS.live_every_min),
    live_from_hour: inRange(data.live_from_hour, 0, 23, BB_API_DEFAULTS.live_from_hour),
    live_to_hour: inRange(data.live_to_hour, 0, 23, BB_API_DEFAULTS.live_to_hour),
    finalize_hour: inRange(data.finalize_hour, 0, 23, BB_API_DEFAULTS.finalize_hour),
  };
}

export async function saveBbApiSettings(settings: BbApiSettings, userId: string): Promise<void> {
  for (const [code, id] of Object.entries(settings.campaigns)) {
    if (!Number.isInteger(id) || id <= 0) throw new Error(`${code} の業務IDは1以上の整数で入力してください`);
  }
  if (settings.live_every_min % 5 !== 0 || settings.live_every_min < 5 || settings.live_every_min > 60) throw new Error('速報の間隔は 5〜60分の、5分きざみで指定してください');
  if (settings.live_from_hour > settings.live_to_hour) throw new Error('速報の時間帯は「開始 ≦ 終了」で指定してください');
  // campaigns は丸ごと置き換える(空欄にしたコードを消すため merge しない)
  await setDoc(doc(getDb(), ...SETTINGS_PATH), {
    campaigns: settings.campaigns,
    schedule_enabled: settings.schedule_enabled,
    live_enabled: settings.live_enabled,
    live_every_min: settings.live_every_min,
    live_from_hour: settings.live_from_hour,
    live_to_hour: settings.live_to_hour,
    finalize_hour: settings.finalize_hour,
    updated_by: userId,
    updated_at: serverTimestamp(),
  });
}

/** 本日の速報(bb_api_live/{date})。サーバーが30分おきに書く。無ければ null */
export async function fetchBbApiLive(date: DateStr): Promise<BbApiLive | null> {
  const snap = await getDoc(doc(getDb(), 'bb_api_live', date));
  return snap.exists() ? (snap.data() as BbApiLive) : null;
}

/**
 * 期間内の「架電の窓」(日付 → 内部ID → 業務コード → 最初と最後の架電)。
 * 1日分の取得結果(bb_api_daily)と本日の速報(bb_api_live)の両方を見て、同じ日は bb_api_daily を優先する
 */
export async function fetchCallWindows(from: DateStr, to: DateStr): Promise<Map<DateStr, Map<string, Record<string, CallWindow>>>> {
  const load = async (name: 'bb_api_live' | 'bb_api_daily') => {
    const snap = await getDocs(query(collection(getDb(), name), where('date', '>=', from), where('date', '<=', to)));
    return snap.docs.map((d) => ({ date: d.id as DateStr, data: d.data() as { operators?: Record<string, { by_code?: Record<string, { first_at?: string | null; last_at?: string | null; calls?: number; success?: number }> }> } }));
  };
  const out = new Map<DateStr, Map<string, Record<string, CallWindow>>>();
  for (const name of ['bb_api_live', 'bb_api_daily'] as const) {
    for (const { date, data } of await load(name)) {
      const w = windowsOfDoc(data);
      if (w.size > 0) out.set(date, w);
    }
  }
  return out;
}

/** 「締め」を手で動かす(管理者だけ)。その日を1日ぶん取り直して入れ替える */
export async function runBbApiFinalize(date: DateStr): Promise<{ date: string; status: string; appointments: number }> {
  const call = httpsCallable<{ date: string }, { date: string; status: string; appointments: number }>(getFirebaseFunctions(), 'bbApiFinalizeNow');
  return (await call({ date })).data;
}

export async function fetchBbApiDaily(date: DateStr): Promise<BbApiDaily | null> {
  const snap = await getDoc(doc(getDb(), 'bb_api_daily', date));
  return snap.exists() ? (snap.data() as BbApiDaily) : null;
}

/** 直近の取得結果(新しい日付が先) */
export async function fetchRecentBbApiDaily(max = 10): Promise<BbApiDaily[]> {
  const snap = await getDocs(query(collection(getDb(), 'bb_api_daily'), orderBy('date', 'desc'), limit(max)));
  return snap.docs.map((d) => d.data() as BbApiDaily);
}

export type BbApiFetchResult = { date: DateStr; status: BbApiDaily['status']; messages: string[]; operators: number };

/** サーバーに1日分の取得を頼む(数分かかることがある) */
export async function requestBbApiFetch(date: DateStr): Promise<BbApiFetchResult> {
  const call = httpsCallable<{ date: DateStr }, BbApiFetchResult>(getFirebaseFunctions(), 'bbApiFetch', { timeout: 540_000 });
  try {
    return (await call({ date })).data;
  } catch (e) {
    const code = (e as { code?: string }).code ?? '';
    const message = e instanceof Error ? e.message : '';
    // サーバー処理が未公開(連携ユーザの設定前)のときは、つながらずに not-found か 小文字の internal になる
    // (サーバーで起きたエラーは大文字の INTERNAL か日本語の文で返るので区別できる)
    if (code === 'functions/not-found' || (code === 'functions/internal' && /^internal( \[\d+\])?$/.test(message))) {
      throw new Error('API連携のサーバー処理がまだ動いていません(連携ユーザの設定後に使えるようになります)');
    }
    throw new Error(message || '取得に失敗しました');
  }
}
